import { ChangeDetectionStrategy, Component, EventEmitter, Input, Output } from '@angular/core';
import { NgFor, NgIf } from '@angular/common';
import { DxSelectBoxModule } from 'devextreme-angular';

import { Vm } from '../../state/integration-tester.models';

type DragPayload = { fromColId: string; reqId: string };

@Component({
	selector: 'it-sidebar',
	standalone: true,
	templateUrl: './it-sidebar.component.html',
	styleUrls: ['./it-sidebar.component.scss'],
	changeDetection: ChangeDetectionStrategy.OnPush,
	imports: [NgIf, NgFor, DxSelectBoxModule],
})
export class ItSidebarComponent {
	@Input({ required: true }) vm!: Vm;

	@Output() requestSearchChanged = new EventEmitter<string>();
	@Output() collectionChanged = new EventEmitter<string | null>();
	@Output() setActiveRequest = new EventEmitter<string>();

	@Output() addCollection = new EventEmitter<void>();
	@Output() renameCollection = new EventEmitter<void>();
	@Output() deleteCollection = new EventEmitter<void>();

	@Output() addRequest = new EventEmitter<void>();
	@Output() duplicateRequest = new EventEmitter<void>();
	@Output() deleteRequest = new EventEmitter<void>();

	@Output() reorderWithinCollection = new EventEmitter<{ colId: string; movingReqId: string; targetReqId: string; place: 'above' | 'below' }>();
	@Output() moveToCollection = new EventEmitter<{ fromColId: string; toColId: string; reqId: string }>();
	@Output() moveToEnd = new EventEmitter<{ colId: string; reqId: string }>();

	@Output() navigateToRequest = new EventEmitter<{ colId: string; reqId: string }>();

	onNavigate(colId: string, reqId: string): void {
		this.navigateToRequest.emit({ colId, reqId });
	}


	dragReq: DragPayload | null = null;
	dragOverReqId: string | null = null;
	dragOverReqPos: 'above' | 'below' | null = null;
	dragOverColId: string | null = null;
	dragOverListEnd = false;

	onCollectionChanged(e: any): void {
		this.collectionChanged.emit((e?.value ?? null) as string | null);
	}


	private isDragging = false;

	onReqDragStart(e: DragEvent, colId: string, reqId: string): void {
		this.isDragging = true;
		this.dragReq = { fromColId: colId, reqId };
		if (e.dataTransfer) {
			e.dataTransfer.effectAllowed = 'move';
			e.dataTransfer.setData('text/plain', JSON.stringify(this.dragReq));
		}
	}


	onReqClick(reqId: string): void {
		// suppress click caused by drag end
		if (this.isDragging) return;
		this.setActiveRequest.emit(reqId);
	}

	onReqDragOver(e: DragEvent, overReqId: string): void {
		e.preventDefault();
		if (e.dataTransfer) e.dataTransfer.dropEffect = 'move';

		this.dragOverReqId = overReqId;

		const el = e.currentTarget as HTMLElement | null;
		if (!el) {
			this.dragOverReqPos = 'above';
			return;
		}

		const rect = el.getBoundingClientRect();
		const mid = rect.top + rect.height / 2;
		this.dragOverReqPos = e.clientY < mid ? 'above' : 'below';
	}

	onReqDragLeave(overReqId: string): void {
		if (this.dragOverReqId === overReqId) {
			this.dragOverReqId = null;
			this.dragOverReqPos = null;
		}
	}

	onReqDropOnReq(e: DragEvent, targetColId: string, targetReqId: string): void {
		e.preventDefault();
		const payload = this.getDragPayload(e);
		if (!payload) return;

		const { fromColId, reqId } = payload;
		if (fromColId !== targetColId) return;
		if (reqId === targetReqId) return;

		const place = this.dragOverReqPos ?? 'above';
		this.reorderWithinCollection.emit({ colId: targetColId, movingReqId: reqId, targetReqId, place });

		this.onReqDragEnd();
	}

	onColDragOver(e: DragEvent, colId: string): void {
		e.preventDefault();
		if (e.dataTransfer) e.dataTransfer.dropEffect = 'move';
		this.dragOverColId = colId;
	}

	onColDragLeave(colId: string): void {
		if (this.dragOverColId === colId) this.dragOverColId = null;
	}

	onColDrop(e: DragEvent, toColId: string): void {
		e.preventDefault();
		const payload = this.getDragPayload(e);
		if (!payload) return;

		const { fromColId, reqId } = payload;
		if (fromColId === toColId) return;

		this.moveToCollection.emit({ fromColId, toColId, reqId });
		this.onReqDragEnd();
	}

	onReqListEndDragOver(e: DragEvent): void {
		e.preventDefault();
		if (e.dataTransfer) e.dataTransfer.dropEffect = 'move';
		this.dragOverListEnd = true;
		this.dragOverReqId = null;
		this.dragOverReqPos = null;
	}

	onReqListEndDragLeave(): void {
		this.dragOverListEnd = false;
	}

	onReqListEndDrop(e: DragEvent, targetColId: string): void {
		e.preventDefault();
		const payload = this.getDragPayload(e);
		if (!payload) return;

		const { fromColId, reqId } = payload;
		if (fromColId !== targetColId) return;

		this.moveToEnd.emit({ colId: targetColId, reqId });
		this.onReqDragEnd();
	}

	private getDragPayload(e: DragEvent): DragPayload | null {
		if (this.dragReq) return this.dragReq;
		try {
			const raw = e.dataTransfer?.getData('text/plain') ?? '';
			const parsed = JSON.parse(raw);
			if (parsed?.fromColId && parsed?.reqId) return parsed;
		} catch { /* ignore */ }
		return null;
	}

	showMovePicker = false;
	movePickerOverColId: string | null = null;

	onMovePickerDragEnter(e: DragEvent): void {
		e.preventDefault();
		if (e.dataTransfer) e.dataTransfer.dropEffect = 'move';
		this.showMovePicker = true;
	}

	onMovePickerDragOver(e: DragEvent): void {
		e.preventDefault();
		if (e.dataTransfer) e.dataTransfer.dropEffect = 'move';
		this.showMovePicker = true;
	}

	// Remove/stop using wrapper dragleave closing. It fires when moving into children during drag.
	onMovePickerDragLeave(_e: DragEvent): void {
		// intentionally do nothing
	}

	// Row hover
	onMovePickerColDragEnter(e: DragEvent, colId: string): void {
		e.preventDefault();
		if (e.dataTransfer) e.dataTransfer.dropEffect = 'move';
		this.movePickerOverColId = colId;
	}

	onMovePickerColDragOver(e: DragEvent, colId: string): void {
		e.preventDefault();
		if (e.dataTransfer) e.dataTransfer.dropEffect = 'move';
		this.movePickerOverColId = colId;
	}

	onMovePickerColDrop(e: DragEvent, toColId: string): void {
		e.preventDefault();
		const payload = this.getDragPayload(e);
		if (!payload) return;

		const { fromColId, reqId } = payload;
		if (fromColId === toColId) return;

		this.moveToCollection.emit({ fromColId, toColId, reqId });

		this.showMovePicker = false;
		this.movePickerOverColId = null;
		this.onReqDragEnd();
	}

	// Ensure drag end closes the picker too
	onReqDragEnd(): void {
		this.isDragging = false;
		this.dragReq = null;
		this.dragOverReqId = null;
		this.dragOverReqPos = null;
		this.dragOverColId = null;
		this.dragOverListEnd = false;

		this.showMovePicker = false;
		this.movePickerOverColId = null;
	}
}
